/**
 * T-50: the expanded spec a feedback run writes for its task. The coding agent puts it in one fenced
 * block in its final message, fenced with four backticks so the spec can hold code blocks of its own:
 *
 * ````factory-spec
 * ## What to build
 * …
 * ````
 *
 * The run's `task.describe` step makes it the task's description and posts the rest of the message.
 */

export const FACTORY_SPEC_LANG = 'factory-spec';

export interface FoundFactorySpec {
  /** The new description, trimmed. */
  spec: string;
  /** The message with the block taken out. */
  rest: string;
}

/**
 * The message's first factory-spec block. It closes at the first line that is the same fence (the same
 * run of backticks or tildes), so a spec fenced with four backticks can hold ``` blocks. Null when there's
 * no block, it isn't closed, or it's empty.
 */
export function findFactorySpec(message: string): FoundFactorySpec | null {
  const lines = message.split(/\r?\n/);
  const start = lines.findIndex((line) => new RegExp(`^\\s*(\`{3,}|~{3,})\\s*${FACTORY_SPEC_LANG}\\s*$`).test(line));
  if (start < 0) return null;
  const fence = lines[start].trim().match(/^(`{3,}|~{3,})/)![1];
  const end = lines.findIndex((line, i) => i > start && line.trim() === fence);
  if (end < 0) return null;
  const spec = lines.slice(start + 1, end).join('\n').trim();
  if (!spec) return null;
  const rest = [...lines.slice(0, start), ...lines.slice(end + 1)].join('\n').replace(/\n{3,}/g, '\n\n').trim();
  return { spec, rest };
}
